import React, { useState, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    WeeklyPlan, ProjectInfo, GlobalUser, Responsible, Profile, CompanyConfig,
    MeetingMinuteRow, MeetingMinuteStatus, WeeklyMeetingMinutesSheet
} from '../../types';

interface MeetingMinutesModalProps {
    isOpen: boolean;
    onClose: () => void;
    plan: WeeklyPlan | null;
    projectInfo: ProjectInfo;
    globalUsers?: GlobalUser[];
    responsibles?: Responsible[];
    profiles?: Profile[];
    companyConfig?: CompanyConfig;
    hasEditPermission?: boolean;
}

export const MeetingMinutesModal: React.FC<MeetingMinutesModalProps> = ({
    isOpen,
    onClose,
    plan,
    projectInfo,
    globalUsers = [],
    responsibles = [],
    profiles = [],
    companyConfig,
    hasEditPermission = true
}) => {
    // Generate initial 20 rows
    const createDefaultRows = (): MeetingMinuteRow[] => {
        return Array.from({ length: 20 }, (_, idx) => ({
            id: `mm-row-${Date.now()}-${idx + 1}`,
            correlative: idx + 1,
            observation: '',
            responsibleName: '',
            isOtherResponsible: false,
            status: 'Pendiente',
            followUp: ''
        }));
    };

    const storageKey = `klydapp_meeting_minutes_${projectInfo?.name || 'default'}_plan_${plan?.id || 0}`;

    // Rows state
    const [rows, setRows] = useState<MeetingMinuteRow[]>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed: WeeklyMeetingMinutesSheet = JSON.parse(saved);
                if (parsed.rows && parsed.rows.length > 0) {
                    return parsed.rows;
                }
            }
        } catch (e) {
            console.error('Error loading meeting minutes sheet:', e);
        }
        return createDefaultRows();
    });

    // Reload when plan changes
    useEffect(() => {
        if (!plan) return;
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed: WeeklyMeetingMinutesSheet = JSON.parse(saved);
                if (parsed.rows && parsed.rows.length > 0) {
                    setRows(parsed.rows);
                    return;
                }
            }
        } catch (e) {
            console.error('Error loading meeting minutes sheet:', e);
        }
        setRows(createDefaultRows());
    }, [plan?.id, projectInfo?.name]);

    // UI States
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveNotification, setSaveNotification] = useState<string | null>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // Active dropdown selector for responsibles
    const [openDropdownRowId, setOpenDropdownRowId] = useState<string | null>(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');

    // Format Date for Title (e.g. DD-MM-YY)
    const formattedDate = useMemo(() => {
        if (!plan?.startDate) return '';
        const parts = plan.startDate.split('-');
        if (parts.length === 3) {
            const [yyyy, mm, dd] = parts;
            return `${dd}-${mm}-${yyyy.slice(-2)}`;
        }
        return plan.startDate;
    }, [plan?.startDate]);

    // Build unified list of users assigned to project
    const availableUsers = useMemo(() => {
        const userMap = new Map<string, { id: string; name: string; role: string; profileId?: string }>();

        // 1. From globalUsers
        globalUsers.filter(u => !u.isDeleted && u.status !== 'Inactive').forEach(u => {
            const assigned = u.assignedProjects?.find(ap => 
                ap.projectId === projectInfo?.id || 
                ap.projectId === projectInfo?.name ||
                (projectInfo?.id && String(ap.projectId).toLowerCase() === String(projectInfo.id).toLowerCase())
            ) || u.assignedProjects?.[0];

            const matchedProfile = assigned 
                ? profiles.find(p => p.id === assigned.profileId || p.name === assigned.profileId) 
                : null;

            let roleName = assigned?.profileName || matchedProfile?.name;
            if (!roleName || roleName === 'Usuario' || roleName === 'Administrador' || roleName === 'SYSADMIN') {
                const respMatch = responsibles.find(r => r.id === u.id || r.email === u.email || r.name === u.name);
                if (respMatch?.profileId) {
                    const profFromResp = profiles.find(p => p.id === respMatch.profileId || p.name === respMatch.profileId);
                    if (profFromResp?.name) roleName = profFromResp.name;
                } else if (respMatch?.role && respMatch.role !== 'Admin' && respMatch.role !== 'Usuario' && respMatch.role !== 'Administrador') {
                    roleName = respMatch.role;
                }
            }

            if (!roleName || roleName === 'Usuario' || roleName === 'Administrador' || roleName === 'SYSADMIN') {
                roleName = profiles[0]?.name || 'Responsable';
            }

            userMap.set(u.id, {
                id: u.id,
                name: u.name,
                role: roleName,
                profileId: assigned?.profileId || matchedProfile?.id
            });
        });

        // 2. From responsibles
        responsibles.forEach(r => {
            const matchedProfile = r.profileId 
                ? profiles.find(p => p.id === r.profileId || p.name === r.profileId) 
                : null;

            let roleName = matchedProfile?.name;
            if (!roleName && r.role && r.role !== 'Admin' && r.role !== 'Usuario' && r.role !== 'Administrador') {
                roleName = r.role;
            }

            if (!userMap.has(r.id)) {
                userMap.set(r.id, {
                    id: r.id,
                    name: r.name,
                    role: roleName || profiles[0]?.name || 'Responsable',
                    profileId: r.profileId || matchedProfile?.id
                });
            } else if (roleName) {
                const existing = userMap.get(r.id)!;
                if (!existing.role || existing.role === 'Usuario' || existing.role === 'Administrador' || existing.role === 'Responsable') {
                    userMap.set(r.id, { ...existing, role: roleName });
                }
            }
        });

        return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    }, [globalUsers, responsibles, profiles, projectInfo]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.user-dropdown-container')) {
                setOpenDropdownRowId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isOpen || !plan) return null;

    // Row handlers
    const handleUpdateRow = (rowId: string, patch: Partial<MeetingMinuteRow>) => {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...patch } : r));
        setHasUnsavedChanges(true);
    };

    const handleSelectResponsible = (rowId: string, user: { id: string; name: string; role: string }) => {
        handleUpdateRow(rowId, {
            responsibleId: user.id,
            responsibleName: user.name,
            isOtherResponsible: false
        });
        setOpenDropdownRowId(null);
        setUserSearchTerm('');
    };

    const handleSelectOtherResponsible = (rowId: string) => {
        handleUpdateRow(rowId, {
            responsibleId: 'other',
            responsibleName: '',
            isOtherResponsible: true
        });
        setOpenDropdownRowId(null);
        setUserSearchTerm('');
    };

    const handleAddRow = () => {
        const newRow: MeetingMinuteRow = {
            id: `mm-row-${Date.now()}-${rows.length + 1}`,
            correlative: rows.length + 1,
            observation: '',
            responsibleName: '',
            isOtherResponsible: false,
            status: 'Pendiente',
            followUp: ''
        };
        setRows(prev => [...prev, newRow]);
        setHasUnsavedChanges(true);
    };

    const handleDeleteRow = (rowId: string) => {
        setRows(prev => {
            const filtered = prev.filter(r => r.id !== rowId);
            return filtered.map((r, idx) => ({ ...r, correlative: idx + 1 }));
        });
        setHasUnsavedChanges(true);
    };

    const handleSaveSheet = () => {
        try {
            const sheetData: WeeklyMeetingMinutesSheet = {
                planId: plan.id,
                projectName: projectInfo?.name || 'Obra',
                startDate: plan.startDate,
                rows,
                updatedAt: new Date().toISOString()
            };
            localStorage.setItem(storageKey, JSON.stringify(sheetData));
            setHasUnsavedChanges(false);
            setSaveNotification('Acta de reunión guardada exitosamente.');
            setTimeout(() => setSaveNotification(null), 3000);
        } catch (e) {
            console.error('Error saving meeting minutes:', e);
            alert('Error al guardar el acta de reunión.');
        }
    };

    // Load image as Base64 for PDF
    const loadImageBase64 = async (url: string): Promise<string | null> => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    // Export to PDF (Landscape A4 for optimal readability of meeting agreements)
    const handleExportPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 12;

            // 1. Logo LD
            const logoSrc = companyConfig?.logo || '/assets/logo_ld.png';
            const logoData = await loadImageBase64(logoSrc);
            if (logoData) {
                try {
                    doc.addImage(logoData, 'PNG', margin, 10, 36, 15, undefined, 'FAST');
                } catch (e) {
                    console.error('Error inserting logo in PDF', e);
                }
            }

            // 2. Encabezado Central
            const obraText = (projectInfo?.name || 'NOMBRE DE OBRA').toUpperCase();
            const semanaText = `ACTA DE REUNIÓN — PLANIFICACIÓN SEMANAL #${plan.id}`;
            const fechaText = `FECHA: ${formattedDate || plan.startDate}`;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text(obraText, pageWidth / 2 + 10, 14, { align: 'center' });

            doc.setFontSize(14);
            doc.text(semanaText, pageWidth / 2 + 10, 20.5, { align: 'center' });

            doc.setFontSize(11);
            doc.text(fechaText, pageWidth / 2 + 10, 26, { align: 'center' });

            // 3. Tabla Formal de Acta de Reunión
            const tableData = rows.map(r => [
                r.correlative.toString(),
                r.observation || '',
                r.responsibleName || '',
                r.status || 'Pendiente',
                r.followUp || ''
            ]);

            autoTable(doc, {
                startY: 30,
                margin: { left: margin, right: margin },
                head: [['#', 'OBSERVACIÓN / COMPROMISO', 'RESPONSABLE', 'ESTADO', 'SEGUIMIENTO']],
                body: tableData,
                theme: 'plain',
                styles: {
                    font: 'helvetica',
                    fontSize: 8.5,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.25,
                    cellPadding: { top: 2.2, bottom: 2.2, left: 3, right: 3 },
                    valign: 'middle'
                },
                headStyles: {
                    fontStyle: 'bold',
                    fontSize: 9.5,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineWidth: 0.35,
                    lineColor: [0, 0, 0],
                    halign: 'left'
                },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
                    1: { cellWidth: 105, halign: 'left' },
                    2: { cellWidth: 50, halign: 'left' },
                    3: { cellWidth: 32, halign: 'center', fontStyle: 'bold' },
                    4: { cellWidth: 74, halign: 'left' }
                },
                didParseCell: (data) => {
                    if (data.section === 'head') {
                        if (data.column.index === 0 || data.column.index === 3) {
                            data.cell.styles.halign = 'center';
                        }
                    }
                    if (data.section === 'body' && data.column.index === 3) {
                        const val = String(data.cell.raw).trim();
                        if (val === 'Ejecutado') {
                            data.cell.styles.textColor = [22, 101, 52]; // Verde
                        } else if (val === 'No ejecutado') {
                            data.cell.styles.textColor = [185, 28, 28]; // Rojo
                        } else {
                            data.cell.styles.textColor = [180, 83, 9]; // Ámbar / Pendiente
                        }
                    }
                }
            });

            // Nombre del archivo PDF
            const cleanProjectName = (projectInfo?.name || 'Obra').replace(/[^a-zA-Z0-9_-]/g, '_');
            doc.save(`Acta_Reunion_Planificacion_Semanal_${plan.id}_${cleanProjectName}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error al generar el archivo PDF del acta de reunión.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200 print:p-0 print:bg-white print:static">
            
            {/* Ventana Única Integrada */}
            <div className="bg-white text-slate-900 rounded-2xl shadow-2xl border border-slate-300 w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden print:h-auto print:shadow-none print:border-none print:rounded-none">
                
                {/* 1. ENCABEZADO SUPERIOR FIJO (Logo + Títulos Centrales + Acciones) */}
                <div className="px-6 py-4 border-b-2 border-black bg-white shrink-0">
                    <div className="relative flex items-center justify-between">
                        
                        {/* Logo LD Izquierda */}
                        <div className="w-32 md:w-36 flex items-center shrink-0">
                            <img
                                src={companyConfig?.logo || '/assets/logo_ld.png'}
                                alt="LD CONSTRUCTORA"
                                className="max-h-12 max-w-full object-contain"
                            />
                        </div>

                        {/* Títulos Centrales Formales */}
                        <div className="flex-1 text-center px-4">
                            <h1 className="text-lg md:text-xl font-black tracking-tight uppercase text-black leading-tight">
                                {projectInfo?.name || 'NOMBRE DE OBRA'}
                            </h1>
                            <h2 className="text-base md:text-lg font-black tracking-tight uppercase text-black mt-0.5">
                                ACTA DE REUNIÓN — PLANIFICACIÓN SEMANAL #{plan.id}
                            </h2>
                            <p className="text-xs md:text-sm font-black uppercase text-black mt-0.5">
                                FECHA: {formattedDate || plan.startDate}
                            </p>
                        </div>

                        {/* Botones de Acción Superiores Derecha */}
                        <div className="flex items-center gap-2 shrink-0 print:hidden">
                            {saveNotification && (
                                <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 animate-in fade-in duration-200">
                                    <span className="material-symbols-outlined text-[15px]">check_circle</span>
                                    {saveNotification}
                                </span>
                            )}

                            {/* Botón Descargar PDF */}
                            <button
                                onClick={handleExportPdf}
                                disabled={isGeneratingPdf}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
                                title="Descargar acta de reunión en PDF"
                            >
                                <span className="material-symbols-outlined text-[17px]">
                                    {isGeneratingPdf ? 'hourglass_top' : 'picture_as_pdf'}
                                </span>
                                <span className="hidden sm:inline">{isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}</span>
                            </button>

                            {/* Botón Guardar */}
                            {hasEditPermission && (
                                <button
                                    onClick={handleSaveSheet}
                                    className={`flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer ${
                                        hasUnsavedChanges ? 'ring-2 ring-emerald-400 ring-offset-1' : ''
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[17px]">save</span>
                                    <span className="hidden sm:inline">Guardar</span>
                                </button>
                            )}

                            {/* Cerrar */}
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
                                title="Cerrar ventana"
                            >
                                <span className="material-symbols-outlined text-[22px]">close</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. TABLA CON ENCABEZADO STICKY Y FILAS DESPLAZABLES */}
                <div className="flex-1 overflow-y-auto bg-white px-6 py-4">
                    <div className="border-2 border-black">
                        <table className="w-full text-left border-collapse text-xs">
                            {/* ENCABEZADO DE TABLA STICKY */}
                            <thead className="sticky top-0 z-20 bg-white shadow-xs">
                                <tr className="border-b-2 border-black bg-white text-black font-black uppercase text-xs tracking-wider">
                                    <th className="border-r-2 border-black px-2 py-2.5 w-12 text-center bg-white">#</th>
                                    <th className="border-r-2 border-black px-3 py-2.5 min-w-[260px] bg-white">OBSERVACIÓN / COMPROMISO</th>
                                    <th className="border-r-2 border-black px-3 py-2.5 min-w-[180px] bg-white">RESPONSABLE</th>
                                    <th className="border-r-2 border-black px-3 py-2.5 w-36 text-center bg-white">ESTADO</th>
                                    <th className="px-3 py-2.5 min-w-[200px] bg-white">SEGUIMIENTO</th>
                                    {hasEditPermission && <th className="px-2 py-2.5 w-10 text-center bg-white print:hidden"></th>}
                                </tr>
                            </thead>
                            
                            {/* FILAS DE LA TABLA */}
                            <tbody className="divide-y divide-black font-medium text-black">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        
                                        {/* 1. # (Correlativo) */}
                                        <td className="border-r-2 border-black px-2 py-2 text-center font-bold text-black bg-white">
                                            {row.correlative}
                                        </td>

                                        {/* 2. OBSERVACIÓN / COMPROMISO (Texto) */}
                                        <td className="border-r-2 border-black px-2 py-1.5 bg-white">
                                            <textarea
                                                value={row.observation}
                                                onChange={e => handleUpdateRow(row.id, { observation: e.target.value })}
                                                disabled={!hasEditPermission}
                                                rows={2}
                                                placeholder="Describir observación o compromiso..."
                                                className="w-full border-none bg-transparent p-1 text-xs text-black font-medium outline-none resize-none focus:bg-blue-50/40 rounded transition-colors placeholder:italic placeholder:text-slate-300"
                                            />
                                        </td>

                                        {/* 3. RESPONSABLE (Dropdown con Buscador) */}
                                        <td className="border-r-2 border-black px-2 py-1.5 bg-white align-middle">
                                            {row.isOtherResponsible ? (
                                                <div className="flex items-center gap-1.5">
                                                    <input
                                                        type="text"
                                                        value={row.responsibleName}
                                                        onChange={e => handleUpdateRow(row.id, { responsibleName: e.target.value })}
                                                        disabled={!hasEditPermission}
                                                        placeholder="Nombre responsable..."
                                                        className="w-full border border-blue-400 bg-blue-50/50 rounded px-2 py-1 text-xs font-bold text-black outline-none focus:ring-1 focus:ring-blue-500"
                                                        autoFocus
                                                    />
                                                    {hasEditPermission && (
                                                        <button
                                                            onClick={() => handleUpdateRow(row.id, { isOtherResponsible: false, responsibleName: '' })}
                                                            className="p-1 text-slate-400 hover:text-blue-600 rounded transition-colors shrink-0 cursor-pointer print:hidden"
                                                            title="Volver a la lista"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="relative user-dropdown-container">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!hasEditPermission) return;
                                                            setOpenDropdownRowId(openDropdownRowId === row.id ? null : row.id);
                                                            setUserSearchTerm('');
                                                        }}
                                                        disabled={!hasEditPermission}
                                                        className={`w-full text-left px-2 py-1 rounded flex items-center justify-between transition-all ${
                                                            row.responsibleName 
                                                                ? 'text-black font-bold' 
                                                                : 'text-slate-400 hover:text-slate-600 italic'
                                                        } ${hasEditPermission ? 'cursor-pointer hover:bg-slate-100' : 'cursor-default'}`}
                                                    >
                                                        <span className="truncate">{row.responsibleName || '—'}</span>
                                                        {hasEditPermission && (
                                                            <span className="material-symbols-outlined text-[14px] text-slate-400 shrink-0 print:hidden">
                                                                expand_more
                                                            </span>
                                                        )}
                                                    </button>

                                                    {/* Dropdown Popover */}
                                                    {openDropdownRowId === row.id && (
                                                        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-2xl border border-slate-300 z-50 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
                                                            {/* Search Bar */}
                                                            <div className="p-2 border-b border-slate-200 bg-slate-50">
                                                                <div className="relative">
                                                                    <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]">search</span>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Buscar responsable..."
                                                                        value={userSearchTerm}
                                                                        onChange={e => setUserSearchTerm(e.target.value)}
                                                                        className="w-full pl-8 pr-2.5 py-1 bg-white border border-slate-300 rounded-lg text-xs font-medium text-black outline-none focus:ring-1 focus:ring-blue-500"
                                                                        autoFocus
                                                                        onClick={e => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* User List */}
                                                            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                                                                {availableUsers
                                                                    .filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.role.toLowerCase().includes(userSearchTerm.toLowerCase()))
                                                                    .map(user => (
                                                                        <button
                                                                            key={user.id}
                                                                            type="button"
                                                                            onClick={() => handleSelectResponsible(row.id, user)}
                                                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between cursor-pointer"
                                                                        >
                                                                            <span className="font-bold text-xs text-black">{user.name}</span>
                                                                            <span className="text-[10px] font-semibold text-slate-500 ml-2 truncate max-w-[110px]">
                                                                                {user.role}
                                                                            </span>
                                                                        </button>
                                                                    ))}

                                                                {availableUsers.filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase())).length === 0 && (
                                                                    <div className="p-3 text-center text-xs text-slate-400 italic">
                                                                        No se encontraron responsables
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Option 'Otro' at bottom */}
                                                            <div className="p-1.5 border-t border-slate-200 bg-slate-50">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectOtherResponsible(row.id)}
                                                                    className="w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 hover:bg-blue-100 flex items-center gap-1.5 transition-colors cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                                                    Otro (Ingresar manualmente)
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>

                                        {/* 4. ESTADO (Pendiente | Ejecutado | No ejecutado) */}
                                        <td className="border-r-2 border-black px-2 py-1.5 bg-white text-center align-middle">
                                            <select
                                                value={row.status}
                                                onChange={e => handleUpdateRow(row.id, { status: e.target.value as MeetingMinuteStatus })}
                                                disabled={!hasEditPermission}
                                                className={`w-full text-center font-bold text-xs px-2 py-1 rounded border transition-all cursor-pointer outline-none ${
                                                    row.status === 'Ejecutado'
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                                        : row.status === 'No ejecutado'
                                                        ? 'bg-red-50 text-red-700 border-red-300'
                                                        : 'bg-amber-50 text-amber-700 border-amber-300'
                                                }`}
                                            >
                                                <option value="Pendiente">Pendiente</option>
                                                <option value="Ejecutado">Ejecutado</option>
                                                <option value="No ejecutado">No ejecutado</option>
                                            </select>
                                        </td>

                                        {/* 5. SEGUIMIENTO (Texto) */}
                                        <td className="px-2 py-1.5 bg-white">
                                            <textarea
                                                value={row.followUp}
                                                onChange={e => handleUpdateRow(row.id, { followUp: e.target.value })}
                                                disabled={!hasEditPermission}
                                                rows={2}
                                                placeholder="Anotar seguimiento o avance..."
                                                className="w-full border-none bg-transparent p-1 text-xs text-black font-medium outline-none resize-none focus:bg-blue-50/40 rounded transition-colors placeholder:italic placeholder:text-slate-300"
                                            />
                                        </td>

                                        {/* Acción Eliminar Fila */}
                                        {hasEditPermission && (
                                            <td className="px-1 py-1.5 text-center bg-white print:hidden">
                                                <button
                                                    onClick={() => handleDeleteRow(row.id)}
                                                    className="p-1 text-slate-300 hover:text-red-600 rounded transition-colors cursor-pointer"
                                                    title="Eliminar fila"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">delete</span>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. PIE DE VENTANA FIJO */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0 print:hidden">
                    <div>
                        {hasEditPermission && (
                            <button
                                onClick={handleAddRow}
                                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 font-bold text-xs rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px] text-blue-600">add_circle</span>
                                + Añadir Fila
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-xs text-slate-500 font-medium">
                            Total Compromisos: <strong className="text-black">{rows.length}</strong>
                        </span>
                        <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
